/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import { ArrowUpTrayIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import moment from "moment";
import useIPMSApi from "./ipms";

interface FileAttachment {
  attachment_id: number;
  file_name: string;
  file_extension: string;
  file_size: number;
  uploaded_at: string;
  description?: string;
}

interface AssessmentFileUploadProps {
  reviewId: number;
  employeeId: number;
  disabled?: boolean;
  onFileUploaded?: (file: FileAttachment) => void;
}

const AssessmentFileUpload: React.FC<AssessmentFileUploadProps> = ({
  reviewId,
  employeeId,
  disabled = false,
  onFileUploaded,
}) => {
  const { fetchAttachments, uploadAssessmentAttachment, deleteAttachment } =
    useIPMSApi();

  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [description, setDescription] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (reviewId) {
      loadAttachments();
    }
  }, [reviewId]);

  const loadAttachments = async () => {
    try {
      setLoadingFiles(true);
      const res = await fetchAttachments(reviewId);
      if (res?.status && res?.data?.attachments) {
        setFiles(res.data.attachments);
      } else if (res?.data?.attachments) {
        setFiles(res.data.attachments);
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
      toast.error("Error loading attachments");
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedExtensions = [".doc", ".docx", ".xls", ".xlsx", ".pdf"];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(
        `Invalid file type. Allowed: ${allowedExtensions.join(", ")}`,
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSelectedFile(null);
      return;
    }

    const maxFileSize = 10 * 1024 * 1024;
    if (file.size > maxFileSize) {
      toast.error("File size exceeds 10 MB limit");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    // 1. Guard against duplicate triggering while upload is in progress
    if (uploading) return;

    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }
    if (!reviewId) {
      toast.error("Review ID is missing");
      return;
    }
    if (!employeeId) {
      toast.error("Employee ID is missing");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("review_id", String(reviewId));
    formData.append("employee_id", String(employeeId));
    if (description.trim()) {
      formData.append("description", description.trim());
    }

    try {
      setUploading(true);
      const res = await uploadAssessmentAttachment(reviewId, formData);

      if (res?.status) {
        // Single toast alert call
        toast.success("File uploaded successfully");
        setDescription("");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";

        await loadAttachments();

        if (onFileUploaded) {
          onFileUploaded(res.data);
        }
      } else {
        toast.error(res?.message || "Upload failed");
      }
    } catch (error: any) {
      console.error("Error uploading file:", error);
      toast.error(error?.response?.data?.detail || "Error uploading file");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (attachmentId: number, fileName: string) => {
    if (!window.confirm(`Delete ${fileName}?`)) return;

    try {
      const res = await deleteAttachment(attachmentId);
      if (res?.status) {
        toast.success("File deleted successfully");
        await loadAttachments();
      } else {
        toast.error("Failed to delete file");
      }
    } catch (error: any) {
      console.error("Error deleting file:", error);
      toast.error(error?.response?.data?.detail || "Error deleting file");
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Fixed Timezone Date Formatting using Moment
  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    return moment(dateString).format("DD/MM/YYYY, h:mm:ss a");
  };

  return (
    <div className="border border-gray-200 rounded-lg p-5 bg-white dark:bg-gray-800 dark:border-gray-700 shadow-sm my-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 border-b pb-2 border-gray-100 dark:border-gray-700">
        Upload Supporting Documents
      </h3>

      {!disabled && (
        <div className="space-y-3">
          <div className="flex flex-col md:flex-row items-center gap-3 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="w-full md:w-1/3">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
                accept=".doc,.docx,.xls,.xlsx,.pdf"
                className="block w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer disabled:opacity-50"
              />
            </div>

            <div className="w-full md:flex-1">
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (e.g. Performance metrics, completion report...)"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md bg-white dark:bg-gray-700 dark:border-gray-500 dark:text-white focus:ring-1 focus:ring-green-500 focus:border-green-500 outline-none"
              />
            </div>

            <div className="w-full md:w-auto shrink-0">
              <button
                type="button"
                onClick={handleFileUpload}
                disabled={uploading || !selectedFile}
                className="w-full md:w-auto inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-white bg-black hover:bg-gray-800 rounded-md transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed shadow-sm"
              >
                <ArrowUpTrayIcon className="w-4 h-4 mr-1.5" />
                {uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>

          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Allowed Formats: DOC, DOCX, XLS, XLSX, PDF | Max size: 10 MB
          </p>
        </div>
      )}

      <div className="mt-5">
        <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider mb-3">
          Uploaded Files ({files.length})
        </h4>

        {loadingFiles ? (
          <p className="text-xs text-gray-500">Loading files...</p>
        ) : files.length === 0 ? (
          <p className="text-xs text-gray-500 italic bg-gray-50 dark:bg-gray-700/30 p-3 rounded text-center border border-dashed border-gray-200 dark:border-gray-600">
            No supporting documents uploaded yet.
          </p>
        ) : (
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.attachment_id}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-sm hover:border-green-300 transition-colors"
              >
                <div className="flex-1 min-w-0 mr-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded">
                      {file.file_extension.replace(".", "")}
                    </span>
                    <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                      {file.file_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    <span>{formatFileSize(file.file_size)}</span>
                    <span>•</span>
                    <span>{formatDate(file.uploaded_at)}</span>
                    {file.description && (
                      <>
                        <span>•</span>
                        <span className="italic text-gray-600 dark:text-gray-300 truncate">
                          "{file.description}"
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {!disabled && (
                  <button
                    type="button"
                    onClick={() =>
                      handleDeleteFile(file.attachment_id, file.file_name)
                    }
                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-gray-600 rounded-md transition-colors shrink-0"
                    title="Delete file"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentFileUpload;
